import React, { useState, useEffect, useCallback } from "react";
import { Briefcase, GraduationCap, ShieldCheck, MapPin, ArrowRight, Loader2, Plus, Edit2, Trash2, X, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import VerificationPanel from "@/components/VerificationPanel";
import OwnerInternDashboard from "@/components/OwnerInternDashboard";
import OwnerInbox from "@/components/OwnerInbox";
import EmailStrip from "@/components/EmailStrip";
import { EMAILS } from "@/lib/content";
import { toast } from "sonner";

export default function CareersPage() {
  const { user } = useAuth();
  const [internRefreshTick, setInternRefreshTick] = useState(0);
  const [prefill, setPrefill] = useState(null); // { role, type, nonce }

  const applyToRole = (opening) => {
    setPrefill({
      role: opening.role,
      type: opening.type === "Internship" ? "Internship" : "Job",
      nonce: Date.now(),
    });
    const el = document.getElementById("apply-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="careers-page">
      <section className="vj-dark vj-radial-fade px-6 lg:px-12 py-20">
        <div className="max-w-[1400px]">
          <div className="overline text-slate-400">Careers</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter leading-[1.05] mt-4 max-w-4xl">
            Engineer the systems that define the next era.
          </h1>
          <p className="text-slate-300 mt-6 max-w-3xl leading-relaxed">
            Apply to open positions, explore internship programs, and verify VAJRON-issued credentials — all from a single
            recruitment portal.
          </p>
        </div>
      </section>

      {/* OWNER DASHBOARD - visible only to owner */}
      {user?.is_owner && <OwnerInternDashboard refreshTick={internRefreshTick} />}
      {user?.is_owner && <OwnerInbox onInternAdded={() => setInternRefreshTick((t) => t + 1)} />}

      {/* OPENINGS */}
      <OpeningsSection isOwner={!!user?.is_owner} onApply={applyToRole} />

      {/* APPLICATION FORM */}
      <section id="apply-section" className="px-6 lg:px-12 py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-[1400px] grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline">02 / Apply</div>
            <h2 className="font-display text-3xl font-extrabold mt-3 tracking-tight">Job Applications & Internship Programs</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Submit your application for any open role or VAJRON internship program. Our team reviews each profile carefully.
            </p>
          </div>
          <div className="lg:col-span-8">
            <ApplicationForm prefill={prefill} />
          </div>
        </div>
      </section>

      {/* VERIFICATION */}
      <section className="px-6 lg:px-12 py-16 max-w-[1400px]">
        <div className="flex items-center gap-2 overline"><ShieldCheck size={14}/> 03 / Credential Verification</div>
        <h2 className="font-display text-3xl font-extrabold mt-3 tracking-tight">Verify VAJRON-issued credentials</h2>
        <p className="text-slate-600 mt-3 max-w-3xl">Anyone can verify a VAJRON internship credential by entering the Credential ID below.</p>
        <div className="mt-7">
          <VerificationPanel />
        </div>
      </section>

      <EmailStrip
        heading="Questions about careers or internships? Write to our recruitment team."
        emails={[{ label: "Careers & Internships", address: EMAILS.careers }]}
      />
    </div>
  );
}

function OpeningsSection({ isOwner, onApply }) {
  const [openings, setOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // opening obj or "new"
  const [stats, setStats] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/openings");
      setOpenings(data);
    } catch (e) {
      // public endpoint; fail silently
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    if (!isOwner) return;
    try {
      const { data } = await api.get("/owner/opening-stats");
      setStats(data || {});
    } catch (e) {
      // ignore
    }
  }, [isOwner]);

  useEffect(() => { load(); loadStats(); }, [load, loadStats]);

  const handleDelete = async (o) => {
    if (!window.confirm(`Delete opening "${o.role}"?`)) return;
    try {
      await api.delete(`/openings/${o.id}`);
      toast.success("Opening removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
    }
  };

  return (
    <section className="px-6 lg:px-12 py-16 max-w-[1400px]" data-testid="openings-section">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline">01 / Open Positions</div>
          <h2 className="font-display text-3xl font-extrabold mt-3 tracking-tight">Current Openings</h2>
        </div>
        {isOwner && (
          <button className="vj-btn-primary" onClick={() => setEditing("new")} data-testid="add-opening-btn">
            <Plus size={14}/> Add Opening
          </button>
        )}
      </div>

      {loading && <div className="mt-8 text-slate-500">Loading openings…</div>}
      {!loading && openings.length === 0 && (
        <div className="mt-8 text-slate-500" data-testid="no-openings">No open positions at the moment.</div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {openings.map((o) => (
          <div key={o.id} className="vj-card p-6 relative group" data-testid={`opening-${o.id}`}>
            <div className="flex items-center gap-2 overline">
              {o.type === "Internship" ? <GraduationCap size={12}/> : <Briefcase size={12}/>}
              {o.type}
            </div>
            <h3 className="font-display font-bold text-lg mt-3 leading-snug pr-12">{o.role}</h3>
            <div className="mt-4 text-sm text-slate-600 flex items-center gap-2"><MapPin size={13}/> {o.location} · {o.mode}</div>
            {isOwner && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E3A8A] bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full" data-testid={`opening-applicants-${o.id}`}>
                <Users size={12}/> {stats[o.role] || 0} applied
              </div>
            )}
            <button
              onClick={() => onApply?.(o)}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A8A] hover:gap-2.5 transition-all"
              data-testid={`apply-opening-${o.id}`}
            >
              Apply for this role <ArrowRight size={14}/>
            </button>
            {isOwner && (
              <div className="absolute top-4 right-4 flex items-center gap-1.5">
                <button onClick={() => setEditing(o)} className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 text-slate-600" title="Edit" data-testid={`edit-opening-${o.id}`}>
                  <Edit2 size={13}/>
                </button>
                <button onClick={() => handleDelete(o)} className="p-1.5 rounded border border-slate-200 hover:bg-red-50 text-red-600" title="Delete" data-testid={`delete-opening-${o.id}`}>
                  <Trash2 size={13}/>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <OpeningModal
          opening={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

function OpeningModal({ opening, onClose, onSaved }) {
  const isNew = !opening;
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    role: opening?.role || "",
    type: opening?.type || "Full-time",
    mode: opening?.mode || "On-site",
    location: opening?.location || "",
  });
  const on = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isNew) await api.post("/openings", form);
      else await api.patch(`/openings/${opening.id}`, form);
      toast.success(isNew ? "Opening added" : "Opening updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(11,17,32,0.55)" }} onClick={onClose} data-testid="opening-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white w-full max-w-lg p-7 rounded-md shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-extrabold tracking-tight">{isNew ? "Add Opening" : "Edit Opening"}</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="opening-modal-close"><X size={18}/></button>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="overline">Role / Title</label>
            <input data-testid="opening-role" className="vj-input mt-1.5" value={form.role} onChange={on("role")} required placeholder="e.g. Drone Systems Engineer"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline">Type</label>
              <select data-testid="opening-type" className="vj-input mt-1.5" value={form.type} onChange={on("type")}>
                {["Full-time", "Internship", "Part-time", "Contract"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Mode</label>
              <select data-testid="opening-mode" className="vj-input mt-1.5" value={form.mode} onChange={on("mode")}>
                {["On-site", "Remote", "Hybrid", "Remote / On-site"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="overline">Location</label>
            <input data-testid="opening-location" className="vj-input mt-1.5" value={form.location} onChange={on("location")} placeholder="e.g. Delhi NCR"/>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="vj-btn-ghost" data-testid="opening-cancel">Cancel</button>
          <button disabled={busy} className="vj-btn-primary" data-testid="opening-save">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <ArrowRight size={14}/>}
            {isNew ? "Add Opening" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ApplicationForm({ prefill }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resume, setResume] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", institute: "", role: "",
    application_type: "Internship", preferred_mode: "On-site", message: "",
  });

  useEffect(() => {
    if (prefill?.role) {
      setForm((s) => ({ ...s, role: prefill.role, application_type: prefill.type || s.application_type }));
    }
  }, [prefill]);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (resume) fd.append("resume", resume);
      await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application submitted. Our team will review your profile.");
      setForm({ name:"", email:"", phone:"", institute:"", role:"", application_type:"Internship", preferred_mode:"On-site", message:""});
      setResume(null);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="vj-card p-7 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="application-form">
      <div className="md:col-span-2">
        <label className="overline">Application Type</label>
        <div className="mt-2 inline-flex border border-slate-200" role="group">
          {["Internship","Job"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setForm((s) => ({ ...s, application_type: t }))}
              data-testid={`app-type-${t.toLowerCase()}`}
              className={`px-5 py-2 text-sm font-semibold ${form.application_type===t? "bg-[#0B1120] text-white":"bg-white text-slate-700"}`}
            >{t}</button>
          ))}
        </div>
      </div>
      <Input label="Full Name" value={form.name} onChange={onChange("name")} required testid="app-name"/>
      <Input label="Email" type="email" value={form.email} onChange={onChange("email")} required testid="app-email"/>
      <Input label="Phone" value={form.phone} onChange={onChange("phone")} testid="app-phone"/>
      <Input label="Institute / Current Company" value={form.institute} onChange={onChange("institute")} testid="app-institute"/>
      <Input label="Role applying for" value={form.role} onChange={onChange("role")} required testid="app-role" className="md:col-span-2"/>
      <div className="md:col-span-2">
        <label className="overline">Preferred Mode</label>
        <select
          data-testid="app-preferred-mode"
          value={form.preferred_mode}
          onChange={onChange("preferred_mode")}
          className="vj-input mt-1.5"
        >
          {["On-site", "Remote", "Hybrid"].map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="overline">Resume / CV <span className="text-slate-400 normal-case">(PDF, DOC, DOCX — optional)</span></label>
        <input
          data-testid="app-resume"
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={(e) => setResume(e.target.files?.[0] || null)}
          className="vj-input mt-1.5"
        />
        {resume && <div className="mt-1.5 text-xs text-slate-500 font-mono">{resume.name} · {(resume.size/1024).toFixed(1)} KB</div>}
      </div>
      <div className="md:col-span-2">
        <label className="overline">Brief Statement</label>
        <textarea data-testid="app-message" value={form.message} onChange={onChange("message")} className="vj-input mt-1.5" placeholder="Tell us about your background, motivations, and relevant projects."/>
      </div>
      {err && <div className="md:col-span-2 text-sm text-red-600">{err}</div>}
      <div className="md:col-span-2">
        <button disabled={busy} className="vj-btn-primary" data-testid="app-submit">
          {busy ? <Loader2 className="animate-spin" size={14}/> : <ArrowRight size={14}/>}
          Submit Application
        </button>
      </div>
    </form>
  );
}

function Input({ label, testid, className = "", ...props }) {
  return (
    <div className={className}>
      <label className="overline">{label}</label>
      <input data-testid={testid} className="vj-input mt-1.5" {...props}/>
    </div>
  );
}
